import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CardConfiguration } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CardActions, LinkCardPayload, UserRecord } from '../../user.model';
import { CardAction, RenewCardPayload, SubscribeCardPayload, UserCardFE } from '../../user.model';
import { UserService } from '../../service/user.abstract.service';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { languageSelector, organisationConfigurationSelector } from 'src/app/global/store/global.selectors';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';

@Component({
    selector: 'app-create-card-dialog',
    templateUrl: './create-not-connected-card-dialog.component.html',
    styleUrls: ['./create-not-connected-card-dialog.component.scss'],
})
export class CreateNotConnectedCardDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();
    CardActions = CardActions;

    userRecordLoaded = false;
    processingSubmit = false;

    public stepIndex = 0;
    public steps: string[] = [];
    public subTypes = [];
    public instructionsStepsInfo: string;
    public instructionsStepsConfirmation: string;

    cardConfig: CardConfiguration;
    cardLabel: string;
    cardIcon: string;

    public form: any = new FormGroup({
        picture: new FormControl('', [Validators.required]),
        firstName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        lastName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        sex: new FormControl('', [Validators.required]),
        address: new FormControl('', [Validators.required]),
        birthDate: new FormControl('', [Validators.required]),
        cardType: new FormControl('', [Validators.required]),
        subType: new FormControl('', []),
        expDate: new FormControl('', [Validators.required]),
        cardNumber: new FormControl('', [Validators.minLength(2)]),
    });

    constructor(
        private translateService: TranslateService,
        private userService: UserService,
        private messageService: MessageService,
        private dialogRef: MatDialogRef<CreateNotConnectedCardDialogComponent>,
        private store: Store<State>,
        @Inject(MAT_DIALOG_DATA) public data: { card: Partial<UserCardFE>; action: CardActions; userRecord: UserRecord },
    ) {}

    userId: string;
    ngOnInit() {
        combineLatest([this.store.select(languageSelector), this.store.select(organisationConfigurationSelector)])
            .pipe(
                map(([lang, orgConfig]) => {
                    this.initForm();
                    (this.translateService.instant('pages.user-cards.create.steps.information.heading'), this.translateService.instant('pages.user-cards.create.steps.confirmation.heading'));

                    this.cardConfig = orgConfig?.support?.cards?.find((c) => c.type === this.data.card.cardType);
                    this.instructionsStepsInfo = this.cardConfig?.identity?.instructions?.steps?.info?.[lang] || '';
                    this.instructionsStepsConfirmation = this.cardConfig?.identity?.instructions?.steps?.confirmation?.[lang] || '';
                    if (this.cardConfig?.availableSubTypes?.length > 0) {
                        const subTypes = [];
                        this.cardConfig?.availableSubTypes.forEach((availableSubType) => {
                            const subType = orgConfig?.subTypes?.find((s) => s.tag === availableSubType);
                            subTypes.push({ label: subType?.name?.[lang], value: subType.tag });
                        });
                        this.subTypes = subTypes;
                        this.form.controls['subType'].setValidators(Validators.required);
                    }
                    this.cardLabel = this.cardConfig.identity?.label[lang];
                    this.cardIcon = this.cardConfig?.identity?.icon || 'icon-my-card';
                }),
            )
            .subscribe();

        this.form.patchValue({
            firstName: this.data.userRecord.firstName || '',
            lastName: this.data.userRecord.lastName || '',
            sex: this.data.userRecord.sex || '',
            picture: this.data.userRecord.picture || '',
            birthDate: this.data.userRecord.birthDate || '',
        });
        this.userId = this.data.userRecord.id;
        if (this.data.userRecord.addresses && this.data.userRecord.addresses[0]) {
            this.form.patchValue({ address: this.data.userRecord.addresses[0] });
        }
        this.userRecordLoaded = true;

        if (this.data.action != CardActions.RENEW) {
            this.form.get('cardNumber').addValidators(Validators.required);
        }
    }

    public initForm() {
        this.form.patchValue({ cardType: this.data.card.cardType });
    }

    public closeDialog() {
        this.dialogRef.close();
    }

    public next() {
        this.stepIndex += 1;
    }

    public back() {
        this.stepIndex -= 1;
    }

    public validateInfoStep() {
        return (
            this.form.controls['address'].valid &&
            this.form.controls['firstName'].valid &&
            this.form.controls['lastName'].valid &&
            this.form.controls['cardType'].valid &&
            this.form.controls['sex'].valid &&
            this.form.controls['birthDate'].valid &&
            this.form.controls['picture'].valid &&
            (this.subTypes.length === 0 || this.form.controls['subType']?.valid) &&
            this.form.controls['cardNumber'].valid &&
            this.form.controls['expDate'].valid
        );
    }

    public validateCardForm() {
        return this.form.valid;
    }

    public submit() {
        this.processingSubmit = true;
        const payload: any = {
            cardType: this.form.get('cardType').value,
            address: this.form.get('address').value,
            firstName: this.form.get('firstName').value,
            lastName: this.form.get('lastName').value,
            photo: this.form.get('picture').value,
            sex: this.form.get('sex').value,
            birthDate: this.form.get('birthDate').value,
            proofOfIdentity: [],
        };
        if (this.form.get('subType').value) {
            payload.subType = this.form.get('subType').value.value;
            payload.cardNumber = this.form.get('cardNumber').value;
            payload.expDate = this.form.get('expDate').value;
        }
        if (this.data.action === CardActions.RENEW) {
            payload._id = this.data.card._id;
            payload.userId = this.userId;
            payload.expDate = this.form.get('expDate').value;
            payload.subType = this.form.get('subType').value.value;
            this.submitRenewal(payload);
        } else {
            this.submitSubscription(payload, this.userId);
        }
    }

    public submitSubscription(payload: SubscribeCardPayload, userId: string) {
        this.userService
            .postSubscribeCard(payload, userId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.closeDialog();
                    this.processingSubmit = false;
                    this.messageService.success(this.translateService.instant('pages.user-cards.create.messages.success'));
                },
                (error) => {
                    this.processingSubmit = false;
                    this.messageService.error(this.translateService.instant('pages.user-cards.create.messages.error'));
                },
            );
    }

    public submitLinkSubscription(payload: LinkCardPayload) {
        this.userService
            .postLinkCard(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.closeDialog();
                    this.processingSubmit = false;
                    this.messageService.success(this.translateService.instant('pages.user-cards.create.messages.success'));
                },
                (error) => {
                    this.processingSubmit = false;
                    this.messageService.error(this.translateService.instant('pages.user-cards.create.messages.error'));
                },
            );
    }

    public submitRenewal(payload: RenewCardPayload) {
        this.userService
            .patchRenewalCard(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.closeDialog();
                    this.processingSubmit = false;
                    this.messageService.success(this.translateService.instant('pages.user-cards.renew.messages.success'));
                },
                (error) => {
                    this.processingSubmit = false;
                    this.messageService.error(this.translateService.instant('pages.user-cards.renew.messages.error'));
                },
            );
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
